/**
 * Community marketplace rules (docs/Decisions.md D68). Mirror of the API's
 * src/services/marketplace/rules.js; marketplace.test.js imports both to prove
 * they agree. The labels and display helpers below the mirror live only here.
 */

/** The marketplace terms a vendor accepts when applying (docs/legal/MarketplaceTerms.md). */
export const TERMS_VERSION = '2026-10-30';

export const ID_RE = /^[A-Za-z0-9-]{8,64}$/;

// ---------------------------------------------------------------------------
// Review policies (D68 item 7)
// ---------------------------------------------------------------------------

/**
 * Platform-level review policies; each category uses one. They decide who
 * may approve a listing in the category and what must be checked first.
 * The org admin always holds the final say (D68 item 7).
 *
 * - delegated: a member of an approver group (D68 item 6) may approve.
 * - requirementsVerified: every listed requirement must be validated.
 * - noteRequired: the approval carries a note saying what was checked.
 * - moneyRewards: whether recommender rewards may be paid in money (M5).
 */
export const REVIEW_POLICIES = Object.freeze({
  standard: Object.freeze({
    delegated: true,
    requirementsVerified: false,
    noteRequired: false,
    moneyRewards: true,
  }),
  restricted: Object.freeze({
    delegated: false,
    requirementsVerified: false,
    noteRequired: true,
    moneyRewards: true,
  }),
  regulated: Object.freeze({
    delegated: true,
    requirementsVerified: true,
    noteRequired: false,
    moneyRewards: true,
  }),
  licensed: Object.freeze({
    delegated: false,
    requirementsVerified: true,
    noteRequired: true,
    moneyRewards: false,
  }),
});

export const REVIEW_POLICY_IDS = Object.keys(REVIEW_POLICIES);

/** What a category may ask a vendor for (D68 item 6). */
export const REQUIREMENT_KINDS = ['license', 'permit', 'insurance', 'document', 'other'];

export const LIMITS = Object.freeze({
  nameMax: 60,
  descriptionMax: 500,
  categoriesPerOwner: 200,
  requirementsPerCategory: 10,
  requirementLabelMax: 80,
  businessNameMax: 80,
  vendorDescriptionMax: 2000,
  servicesMax: 50,
  serviceNameMax: 80,
  serviceDescriptionMax: 300,
  priceMaxCents: 100_000_000,
  categoriesPerListing: 10,
  requirementValueMax: 200,
  noteMax: 1000,
  urlMax: 500,
  serviceAreaMax: 120,
});

// ---------------------------------------------------------------------------
// Fees (D68 items 1, 2 and 5)
// ---------------------------------------------------------------------------

/**
 * The platform admin's bounds (Platform admin -> Policies, the D53 layers,
 * section `marketplace`). src/shared/security/policy-settings.js holds the
 * same defaults; a test keeps them equal.
 */
export const DEFAULT_FEE_POLICY = Object.freeze({
  platformFeePercent: 25,
  platformFeeMinCents: 0,
  platformFeeMaxCents: 100_000,
  maxLeadFeeCents: 1_000_000,
  maxLeadFeePercent: 10,
  maxOrgFeeCents: 500_000,
  maxOrgFeePercent: 50,
  maxRewardPercent: 20,
});

export const FEE_TYPES = ['fixed', 'percent'];

function isWholeCents(n) {
  return Number.isInteger(n) && n >= 0;
}

function isPercent(n, max) {
  return (
    typeof n === 'number' &&
    Number.isFinite(n) &&
    n >= 0 &&
    n <= max &&
    Math.abs(n * 100 - Math.round(n * 100)) < 1e-6
  );
}

function money(cents) {
  return (cents / 100).toFixed(2);
}

/**
 * Validate one fee setting: `{ type: 'fixed', amountCents }` or
 * `{ type: 'percent', percent }`. Returns `{ value, error }`.
 */
function validateFee(input, { maxCents, maxPercent, fixedOnly, of }) {
  if (input === null || input === undefined) return { value: null, error: null };
  if (typeof input !== 'object' || Array.isArray(input)) {
    return { value: null, error: 'Choose a fixed amount or a percentage' };
  }
  if (!FEE_TYPES.includes(input.type)) {
    return { value: null, error: 'Choose a fixed amount or a percentage' };
  }
  if (input.type === 'percent') {
    if (fixedOnly) {
      return {
        value: null,
        error: 'Investment organizations charge flat fees only (docs/Decisions.md D68 item 5)',
      };
    }
    if (!isPercent(input.percent, maxPercent)) {
      return {
        value: null,
        error: `Enter a percentage of ${of} from 0 to ${maxPercent}, with at most two decimals`,
      };
    }
    return { value: { type: 'percent', percent: input.percent }, error: null };
  }
  if (!isWholeCents(input.amountCents) || input.amountCents > maxCents) {
    return { value: null, error: `Enter an amount from 0.00 to ${money(maxCents)}` };
  }
  return { value: { type: 'fixed', amountCents: input.amountCents }, error: null };
}

/**
 * The lead fee and organization fee of an org category, inside the platform
 * admin's bounds. Both null means the category charges nothing.
 */
export function validateCategoryFees(
  input,
  { investment = false, policy = DEFAULT_FEE_POLICY } = {}
) {
  const fields = {};
  const lead = validateFee(input?.leadFee, {
    maxCents: policy.maxLeadFeeCents,
    maxPercent: policy.maxLeadFeePercent,
    fixedOnly: investment,
    of: 'the deal value',
  });
  if (lead.error) fields.leadFee = lead.error;
  const org = validateFee(input?.orgFee, {
    maxCents: policy.maxOrgFeeCents,
    maxPercent: policy.maxOrgFeePercent,
    fixedOnly: investment,
    of: 'the lead fee',
  });
  if (org.error) fields.orgFee = org.error;
  if (!fields.orgFee && org.value && !lead.value) {
    fields.orgFee = 'Set a lead fee first: the organization fee is part of it';
  }
  if (
    !fields.orgFee &&
    !fields.leadFee &&
    org.value?.type === 'fixed' &&
    lead.value?.type === 'fixed' &&
    org.value.amountCents > lead.value.amountCents
  ) {
    fields.orgFee = 'The organization fee cannot be more than the lead fee';
  }
  return { value: { leadFee: lead.value, orgFee: org.value }, fields };
}

// ---------------------------------------------------------------------------
// Categories (D68 items 5, 6, 7 and 9)
// ---------------------------------------------------------------------------

function cleanText(value, max) {
  if (typeof value !== 'string') return '';
  return value
    .trim()
    .replace(/\s+/g, ' ')
    .slice(0, max + 1);
}

function validateRequirements(input) {
  if (input === undefined || input === null) return { value: [], error: null };
  if (!Array.isArray(input)) return { value: [], error: 'Requirements must be a list' };
  if (input.length > LIMITS.requirementsPerCategory) {
    return { value: [], error: `At most ${LIMITS.requirementsPerCategory} requirements` };
  }
  const seen = new Set();
  const value = [];
  for (const [i, r] of input.entries()) {
    const label = cleanText(r?.label, LIMITS.requirementLabelMax);
    if (label.length < 2 || label.length > LIMITS.requirementLabelMax) {
      return { value: [], error: `Requirement ${i + 1} needs a name of 2 to 80 characters` };
    }
    if (!REQUIREMENT_KINDS.includes(r?.kind)) {
      return { value: [], error: `Requirement ${i + 1}: choose ${REQUIREMENT_KINDS.join(', ')}` };
    }
    let requirementId = typeof r.requirementId === 'string' ? r.requirementId : '';
    if (!/^[a-z0-9-]{1,40}$/.test(requirementId)) {
      requirementId =
        label
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-|-$/g, '')
          .slice(0, 36) || `req-${i + 1}`;
    }
    while (seen.has(requirementId)) requirementId = `${requirementId}-${i + 1}`.slice(0, 40);
    seen.add(requirementId);
    value.push({ requirementId, label, kind: r.kind });
  }
  return { value, error: null };
}

/**
 * Validate a category create or update.
 *
 * @param {object} input
 * @param {{ owner: 'platform' | 'org', investment?: boolean, policy?: object, isCreate?: boolean }} ctx
 * @returns {{ value: object, fields: object }} fields empty means valid
 */
export function validateCategory(
  input,
  { owner, investment = false, policy, isCreate = true } = {}
) {
  const fields = {};
  const value = {};
  const has = key => input && input[key] !== undefined;

  if (isCreate || has('name')) {
    const name = cleanText(input?.name, LIMITS.nameMax);
    if (name.length < 2 || name.length > LIMITS.nameMax) {
      fields.name = `Use 2 to ${LIMITS.nameMax} characters`;
    } else value.name = name;
  }
  if (has('description')) {
    const description = cleanText(input.description, LIMITS.descriptionMax);
    if (description.length > LIMITS.descriptionMax) {
      fields.description = `At most ${LIMITS.descriptionMax} characters`;
    } else value.description = description;
  }
  if (isCreate || has('parentId')) {
    const parentId = input?.parentId ?? null;
    if (parentId !== null && (typeof parentId !== 'string' || !ID_RE.test(parentId))) {
      fields.parentId = 'Choose a top-level category';
    } else value.parentId = parentId;
  }
  if (has('active')) value.active = Boolean(input.active);
  if (has('sortOrder')) {
    if (!Number.isInteger(input.sortOrder) || input.sortOrder < 0 || input.sortOrder > 9999) {
      fields.sortOrder = 'Use a whole number from 0 to 9999';
    } else value.sortOrder = input.sortOrder;
  }
  if (isCreate || has('reviewPolicy')) {
    const reviewPolicy = input?.reviewPolicy ?? 'standard';
    if (!REVIEW_POLICY_IDS.includes(reviewPolicy)) {
      fields.reviewPolicy = `Choose ${REVIEW_POLICY_IDS.join(', ')}`;
    } else value.reviewPolicy = reviewPolicy;
  }
  if (isCreate || has('requirements')) {
    const req = validateRequirements(input?.requirements);
    if (req.error) fields.requirements = req.error;
    else value.requirements = req.value;
  }

  if (owner === 'platform') {
    if (has('investmentAllowed')) value.investmentAllowed = Boolean(input.investmentAllowed);
    else if (isCreate) value.investmentAllowed = false;
    if (has('leadFee') || has('orgFee')) {
      fields.leadFee = 'Fees are set by each organization';
    }
  } else {
    if (has('investmentAllowed')) {
      fields.investmentAllowed =
        'Only a platform admin marks categories for investment organizations';
    }
    if (isCreate || has('leadFee') || has('orgFee')) {
      const fees = validateCategoryFees(
        { leadFee: input?.leadFee ?? null, orgFee: input?.orgFee ?? null },
        { investment, policy }
      );
      Object.assign(fields, fees.fields);
      if (!Object.keys(fees.fields).length) {
        value.leadFee = fees.value.leadFee;
        value.orgFee = fees.value.orgFee;
      }
    }
  }
  if (
    value.reviewPolicy &&
    REVIEW_POLICIES[value.reviewPolicy].requirementsVerified &&
    Array.isArray(value.requirements) &&
    value.requirements.length === 0
  ) {
    fields.requirements = 'A regulated or licensed category lists at least one requirement';
  }
  return { value, fields };
}

/**
 * Arrange flat categories into a two-level tree (top level sorted by
 * sortOrder then name; each with `children`). Orphans join the top level.
 */
export function categoryTree(categories) {
  const byOrder = (a, b) =>
    (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || String(a.name).localeCompare(String(b.name));
  const ids = new Set(categories.map(c => c.categoryId));
  const top = categories.filter(c => !c.parentId || !ids.has(c.parentId)).sort(byOrder);
  return top.map(c => ({
    ...c,
    children: categories.filter(k => k.parentId === c.categoryId).sort(byOrder),
  }));
}

/**
 * The requirements and policies that apply to a listing's categories: the
 * strictest policy decides delegation and notes; every requirement of every
 * category is listed once (keyed `categoryId:requirementId`).
 */
export function listingReview(categories) {
  const policies = categories.map(c => REVIEW_POLICIES[c.reviewPolicy] || REVIEW_POLICIES.standard);
  const requirements = [];
  for (const c of categories) {
    for (const r of c.requirements || []) {
      requirements.push({
        key: `${c.categoryId}:${r.requirementId}`,
        categoryId: c.categoryId,
        categoryName: c.name,
        ...r,
      });
    }
  }
  return {
    delegated: policies.every(p => p.delegated),
    noteRequired: policies.some(p => p.noteRequired),
    requirementsVerified: policies.some(p => p.requirementsVerified),
    moneyRewards: policies.every(p => p.moneyRewards),
    requirements,
  };
}

// ---------------------------------------------------------------------------
// Vendor profiles (D68 items 9 and 10)
// ---------------------------------------------------------------------------

export const VENDOR_SCOPES = ['platform', 'org'];
export const PRICE_TYPES = ['fixed', 'from', 'quote'];
export const PRICE_UNITS = ['one-time', 'hour', 'month', 'year', 'project'];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^[+()0-9 .-]{7,25}$/;

function validUrl(value) {
  if (typeof value !== 'string' || value.length > LIMITS.urlMax) return false;
  try {
    const u = new URL(value);
    return u.protocol === 'https:';
  } catch {
    return false;
  }
}

/** One row of the services list. Returns `{ value, error }`. */
export function validateService(input) {
  const name = cleanText(input?.name, LIMITS.serviceNameMax);
  if (name.length < 2 || name.length > LIMITS.serviceNameMax) {
    return { value: null, error: `Name: use 2 to ${LIMITS.serviceNameMax} characters` };
  }
  const description = cleanText(input?.description, LIMITS.serviceDescriptionMax);
  if (description.length > LIMITS.serviceDescriptionMax) {
    return {
      value: null,
      error: `Description: at most ${LIMITS.serviceDescriptionMax} characters`,
    };
  }
  const priceType = input?.priceType || 'quote';
  if (!PRICE_TYPES.includes(priceType)) {
    return { value: null, error: `Price type: use ${PRICE_TYPES.join(', ')}` };
  }
  const unit = input?.unit || 'one-time';
  if (!PRICE_UNITS.includes(unit)) {
    return { value: null, error: `Unit: use ${PRICE_UNITS.join(', ')}` };
  }
  let priceCents = null;
  if (priceType !== 'quote') {
    if (!isWholeCents(input?.priceCents) || input.priceCents > LIMITS.priceMaxCents) {
      return { value: null, error: 'Price: enter an amount such as 150 or 150.00' };
    }
    priceCents = input.priceCents;
  }
  return { value: { name, description, priceType, priceCents, unit }, error: null };
}

/**
 * Validate a vendor profile create or update. `fields` holds the errors by
 * field; `services` errors are by row (`services.3`).
 */
export function validateVendor(input, { isCreate = true } = {}) {
  const fields = {};
  const value = {};
  const has = key => input && input[key] !== undefined;

  if (isCreate || has('businessName')) {
    const businessName = cleanText(input?.businessName, LIMITS.businessNameMax);
    if (businessName.length < 2 || businessName.length > LIMITS.businessNameMax) {
      fields.businessName = `Use 2 to ${LIMITS.businessNameMax} characters`;
    } else value.businessName = businessName;
  }
  if (isCreate) {
    const scope = input?.scope || 'platform';
    if (!VENDOR_SCOPES.includes(scope)) fields.scope = 'Choose a platform or organization profile';
    else value.scope = scope;
  } else if (has('scope')) {
    fields.scope = 'A profile cannot change between platform and organization';
  }
  if (has('description')) {
    if (
      typeof input.description !== 'string' ||
      input.description.length > LIMITS.vendorDescriptionMax
    ) {
      fields.description = `At most ${LIMITS.vendorDescriptionMax} characters`;
    } else value.description = input.description.trim();
  }
  if (has('logoUrl')) {
    if (input.logoUrl && !validUrl(input.logoUrl)) {
      fields.logoUrl = 'Use an https:// image address';
    } else value.logoUrl = input.logoUrl || null;
  }
  if (has('contact')) {
    const c = input.contact || {};
    const contact = {};
    if (c.email) {
      if (typeof c.email !== 'string' || !EMAIL_RE.test(c.email) || c.email.length > 254) {
        fields['contact.email'] = 'Enter a valid email address';
      } else contact.email = c.email.trim().toLowerCase();
    }
    if (c.phone) {
      if (typeof c.phone !== 'string' || !PHONE_RE.test(c.phone.trim())) {
        fields['contact.phone'] = 'Enter a phone number of 7 to 25 digits';
      } else contact.phone = c.phone.trim();
    }
    if (c.website) {
      if (!validUrl(c.website)) fields['contact.website'] = 'Use an https:// address';
      else contact.website = c.website;
    }
    if (c.serviceArea) {
      const area = cleanText(c.serviceArea, LIMITS.serviceAreaMax);
      if (area.length > LIMITS.serviceAreaMax) {
        fields['contact.serviceArea'] = `At most ${LIMITS.serviceAreaMax} characters`;
      } else contact.serviceArea = area;
    }
    value.contact = contact;
  }
  if (has('services')) {
    if (!Array.isArray(input.services)) fields.services = 'Services must be a list';
    else if (input.services.length > LIMITS.servicesMax) {
      fields.services = `At most ${LIMITS.servicesMax} services`;
    } else {
      const services = [];
      input.services.forEach((s, i) => {
        const r = validateService(s);
        if (r.error) fields[`services.${i}`] = r.error;
        else services.push(r.value);
      });
      value.services = services;
    }
  }
  if (has('discoverable')) value.discoverable = Boolean(input.discoverable);
  return { value, fields };
}

// ---------------------------------------------------------------------------
// The services list as CSV (docs/MarketplaceDesign.md section 3.3)
// ---------------------------------------------------------------------------

export const CSV_COLUMNS = ['name', 'description', 'price_type', 'price', 'unit'];

/** Split CSV text into rows of cells (quotes, doubled quotes, CRLF). */
export function parseCsv(text) {
  const rows = [];
  let row = [];
  let cell = '';
  let quoted = false;
  const src = String(text ?? '').replace(/^\uFEFF/, '');
  for (let i = 0; i < src.length; i += 1) {
    const ch = src[i];
    if (quoted) {
      if (ch === '"' && src[i + 1] === '"') {
        cell += '"';
        i += 1;
      } else if (ch === '"') quoted = false;
      else cell += ch;
    } else if (ch === '"') quoted = true;
    else if (ch === ',') {
      row.push(cell);
      cell = '';
    } else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && src[i + 1] === '\n') i += 1;
      row.push(cell);
      rows.push(row);
      row = [];
      cell = '';
    } else cell += ch;
  }
  if (cell !== '' || row.length) {
    row.push(cell);
    rows.push(row);
  }
  return rows.filter(r => r.some(c => c.trim() !== ''));
}

/** "150", "150.00", "$1,500.5" -> cents; NaN when not an amount. */
export function amountToCents(raw) {
  const s = String(raw ?? '')
    .trim()
    .replace(/[$,\s]/g, '');
  if (!/^\d+(\.\d{1,2})?$/.test(s)) return Number.NaN;
  const [whole, frac = ''] = s.split('.');
  return Number(whole) * 100 + Number(`${frac}00`.slice(0, 2));
}

/**
 * Turn an uploaded services CSV into service rows. The header row names the
 * columns (CSV_COLUMNS, any order; `name` is required). Errors are by row
 * number as the vendor sees it in a spreadsheet (the header is row 1).
 */
export function servicesFromCsv(text) {
  const rows = parseCsv(text);
  if (!rows.length) return { services: [], errors: [{ row: 1, message: 'The file is empty' }] };
  const header = rows[0].map(h => h.trim().toLowerCase().replace(/\s+/g, '_'));
  if (!header.includes('name')) {
    return {
      services: [],
      errors: [
        { row: 1, message: `The first row must name the columns: ${CSV_COLUMNS.join(', ')}` },
      ],
    };
  }
  const unknown = header.filter(h => h && !CSV_COLUMNS.includes(h));
  if (unknown.length) {
    return { services: [], errors: [{ row: 1, message: `Unknown column: ${unknown.join(', ')}` }] };
  }
  const services = [];
  const errors = [];
  const body = rows.slice(1);
  if (body.length > LIMITS.servicesMax) {
    errors.push({ row: LIMITS.servicesMax + 2, message: `At most ${LIMITS.servicesMax} services` });
  }
  body.slice(0, LIMITS.servicesMax).forEach((cells, i) => {
    const get = col => {
      const idx = header.indexOf(col);
      return idx === -1 ? '' : String(cells[idx] ?? '').trim();
    };
    const priceType = (get('price_type') || (get('price') ? 'fixed' : 'quote')).toLowerCase();
    const priceCents = get('price') ? amountToCents(get('price')) : null;
    const r = validateService({
      name: get('name'),
      description: get('description'),
      priceType,
      priceCents: priceType === 'quote' ? null : priceCents,
      unit: (get('unit') || 'one-time').toLowerCase(),
    });
    if (r.error) errors.push({ row: i + 2, message: r.error });
    else services.push(r.value);
  });
  return { services, errors };
}

// ---------------------------------------------------------------------------
// Listings (D68 items 6 and 9)
// ---------------------------------------------------------------------------

export const LISTING_STATUSES = [
  'invited',
  'pending',
  'approved',
  'declined',
  'suspended',
  'removed',
  'withdrawn',
];

/** Statuses a vendor may (re)apply from; applying moves the listing to pending. */
export const APPLY_FROM = [null, 'invited', 'declined', 'withdrawn'];

/**
 * Roles: `vendor` (owner of the vendor profile), `approver` (a member of a
 * delegated approver group), `orgAdmin`, `platform`. A policy that is not
 * delegated (restricted, licensed) drops `approver` from approve and decline;
 * the handler checks that with `listingReview`.
 */
export const LISTING_ACTIONS = Object.freeze({
  approve: { by: ['approver', 'orgAdmin', 'platform'], from: ['pending'], to: 'approved' },
  decline: {
    by: ['approver', 'orgAdmin', 'platform'],
    from: ['pending'],
    to: 'declined',
    noteRequired: true,
  },
  suspend: {
    by: ['orgAdmin', 'platform'],
    from: ['approved'],
    to: 'suspended',
    noteRequired: true,
  },
  reinstate: { by: ['orgAdmin', 'platform'], from: ['suspended'], to: 'approved' },
  remove: {
    by: ['orgAdmin', 'platform'],
    from: ['approved', 'suspended'],
    to: 'removed',
    noteRequired: true,
  },
  withdraw: {
    by: ['vendor'],
    from: ['invited', 'pending', 'approved', 'suspended'],
    to: 'withdrawn',
  },
});

export function allowedListingActions(status, roles, { delegated = true } = {}) {
  return Object.entries(LISTING_ACTIONS)
    .filter(([name, a]) => {
      if (!a.from.includes(status)) return false;
      const by =
        !delegated && (name === 'approve' || name === 'decline')
          ? a.by.filter(r => r !== 'approver')
          : a.by;
      return by.some(r => roles.includes(r));
    })
    .map(([name]) => name);
}

/**
 * What still stands in the way of approving a listing: an empty list means
 * it can be approved. `checks` maps `categoryId:requirementId` to
 * `{ verified: true }` as the approver ticks them.
 */
export function approvalBlockers(review, { checks = {}, note = '' } = {}) {
  const blockers = [];
  if (review.requirementsVerified) {
    for (const r of review.requirements) {
      if (!checks[r.key]?.verified) blockers.push(`Validate "${r.label}" for ${r.categoryName}`);
    }
  }
  if (review.noteRequired && !String(note || '').trim()) {
    blockers.push('Add a note saying what you checked');
  }
  return blockers;
}

/** The value a vendor gives for each requirement when applying. */
export function validateRequirementAnswers(review, answers) {
  const fields = {};
  const value = {};
  const given = answers && typeof answers === 'object' && !Array.isArray(answers) ? answers : {};
  for (const r of review.requirements) {
    const raw = given[r.key];
    const text = typeof raw === 'string' ? raw.trim() : '';
    if (!text) fields[`requirements.${r.key}`] = `Give the ${r.label.toLowerCase()} details`;
    else if (text.length > LIMITS.requirementValueMax) {
      fields[`requirements.${r.key}`] = `At most ${LIMITS.requirementValueMax} characters`;
    } else value[r.key] = text;
  }
  return { value, fields };
}

/** The listing id for a vendor in an org: one listing per pair. */
export function listingIdFor(orgId, vendorId) {
  return `${orgId}_${vendorId}`;
}

// ---------------------------------------------------------------------------
// Leads: interest lists and the sales tracker (D68 items 1, 3 and 8; M2)
// ---------------------------------------------------------------------------

/** Follow-ups go out on a won close, or this many days after the lead was registered (D68 item 8). */
export const FOLLOW_UP_DAYS = 14;
/** Sign-ups a member can make per day, so lists stay real and fees fair. */
export const DAILY_LEAD_CAP = 10;
export const SALESPEOPLE_MAX = 20;

export const LEAD_STATUSES = ['new', 'qualified', 'won', 'lost', 'withdrawn', 'voided'];
export const OPEN_LEAD_STATUSES = ['new', 'qualified'];
export const LOST_REASONS = ['no-response', 'not-a-fit', 'chose-another', 'price', 'other'];

/**
 * Roles: `vendor` (the vendor profile's owner), `member` (who joined the
 * list), `orgAdmin` and `platform`. Voiding a lead (a fake or duplicate
 * sign-up) is the organization's call and credits its fee from M4 on.
 */
export const LEAD_ACTIONS = Object.freeze({
  qualify: { by: ['vendor'], from: ['new'], to: 'qualified' },
  win: { by: ['vendor'], from: ['new', 'qualified'], to: 'won' },
  lose: { by: ['vendor'], from: ['new', 'qualified'], to: 'lost' },
  assign: { by: ['vendor'], from: ['new', 'qualified'], to: null },
  withdraw: { by: ['member'], from: ['new', 'qualified'], to: 'withdrawn' },
  void: {
    by: ['orgAdmin', 'platform'],
    from: ['new', 'qualified', 'won', 'lost'],
    to: 'voided',
    noteRequired: true,
  },
});

export function allowedLeadActions(status, roles) {
  return Object.entries(LEAD_ACTIONS)
    .filter(([, a]) => a.from.includes(status) && a.by.some(r => roles.includes(r)))
    .map(([name]) => name);
}

/**
 * The salespeople and brokers a vendor names on a listing (D68 item 3). One
 * may be the vendor itself (`self`), which is never charged the balance.
 */
export function validateSalespeople(input) {
  if (!Array.isArray(input)) return { value: [], error: 'Salespeople must be a list' };
  if (input.length > SALESPEOPLE_MAX)
    return { value: [], error: `At most ${SALESPEOPLE_MAX} salespeople` };
  const value = [];
  const ids = new Set();
  for (const [i, s] of input.entries()) {
    const name = cleanText(s?.name, LIMITS.businessNameMax);
    if (name.length < 2 || name.length > LIMITS.businessNameMax) {
      return {
        value: [],
        error: `Salesperson ${i + 1}: give a name of 2 to ${LIMITS.businessNameMax} characters`,
      };
    }
    const email = typeof s?.email === 'string' ? s.email.trim().toLowerCase() : '';
    if (email && (!EMAIL_RE.test(email) || email.length > 254)) {
      return { value: [], error: `Salesperson ${i + 1}: enter a valid email address` };
    }
    let salespersonId =
      typeof s?.salespersonId === 'string' && /^[a-z0-9-]{1,40}$/.test(s.salespersonId)
        ? s.salespersonId
        : '';
    if (!salespersonId) {
      salespersonId = `sp-${
        name
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-|-$/g, '')
          .slice(0, 30) || i + 1
      }`;
    }
    while (ids.has(salespersonId)) salespersonId = `${salespersonId}-${i + 1}`.slice(0, 40);
    ids.add(salespersonId);
    value.push({ salespersonId, name, email: email || null, self: Boolean(s?.self) });
  }
  if (value.filter(s => s.self).length > 1)
    return { value: [], error: 'Only one entry can be the vendor itself' };
  return { value, error: null };
}

/** Counts per status and the conversion rates of a set of leads. */
export function leadSummary(leads) {
  const counts = Object.fromEntries(LEAD_STATUSES.map(s => [s, 0]));
  let dealValueCents = 0;
  for (const l of leads) {
    if (counts[l.status] !== undefined) counts[l.status] += 1;
    if (l.status === 'won' && Number.isInteger(l.dealValueCents))
      dealValueCents += l.dealValueCents;
  }
  const real = leads.length - counts.voided;
  const reached = counts.qualified + counts.won + counts.lost;
  const closed = counts.won + counts.lost;
  const pct = (a, b) => (b ? Math.round((a / b) * 1000) / 10 : null);
  return {
    total: real,
    counts,
    dealValueCents,
    qualifiedRate: pct(reached, real - counts.withdrawn),
    winRate: pct(counts.won, closed),
  };
}

/** When a lead's follow-up is due (D68 item 8): now on a won close, else 14 days after it was registered. */
export function followUpDueAt(createdAt, { wonAt = null } = {}) {
  if (wonAt) return wonAt;
  return new Date(new Date(createdAt).getTime() + FOLLOW_UP_DAYS * 86_400_000).toISOString();
}

// ---------------------------------------------------------------------------
// Follow-ups, reviews, recommendations and the quality queue (M3)
// ---------------------------------------------------------------------------

export const PURCHASE_ANSWERS = ['yes', 'no', 'not-yet'];
/** Reported prices more than this far apart are a mismatch (design section 3.6). */
export const PRICE_TOLERANCE = 0.2;
/** Unresolved mismatches at which a listing is flagged for review (CR-43). */
export const MISMATCH_REVIEW_THRESHOLD = 3;
export const REVIEW_TEXT_MAX = 2000;
export const RESPONSE_TEXT_MAX = 1000;
/**
 * The only reasons a review may be hidden (CR-35, the FTC rule on consumer
 * reviews): never for being negative.
 */
export const REVIEW_HIDE_REASONS = ['abuse', 'personal-data', 'off-topic', 'conflict-of-interest'];

/**
 * The member's follow-up answers: did they buy, about what they paid, an
 * optional review (only a buyer can review) and whether to recommend.
 */
export function validateFollowUp(input) {
  const fields = {};
  const value = {};
  if (!PURCHASE_ANSWERS.includes(input?.purchased))
    fields.purchased = 'Say whether you bought the service';
  else value.purchased = input.purchased;
  if (input?.pricePaidCents !== undefined && input.pricePaidCents !== null) {
    if (!isWholeCents(input.pricePaidCents) || input.pricePaidCents > LIMITS.priceMaxCents) {
      fields.pricePaidCents = 'Enter an amount such as 1200 or 1200.00';
    } else if (value.purchased !== 'yes') {
      fields.pricePaidCents = 'Only give a price for a service you bought';
    } else value.pricePaidCents = input.pricePaidCents;
  }
  if (input?.review !== undefined && input.review !== null) {
    const r = input.review;
    if (value.purchased !== 'yes')
      fields.review = 'Only members who bought the service can review it';
    else if (!Number.isInteger(r?.rating) || r.rating < 1 || r.rating > 5)
      fields['review.rating'] = 'Choose 1 to 5 stars';
    else {
      const text = typeof r.text === 'string' ? r.text.trim() : '';
      if (text.length > REVIEW_TEXT_MAX)
        fields['review.text'] = `At most ${REVIEW_TEXT_MAX} characters`;
      else value.review = { rating: r.rating, text };
    }
  }
  if (input?.recommend !== undefined) value.recommend = Boolean(input.recommend);
  return { value, fields };
}

/**
 * Compare the member's answer with the vendor's tracker (design section 3.6,
 * CR-43). Returns the mismatch kind or null.
 */
export function mismatchOf(lead) {
  const purchased = lead.followUp?.purchased;
  if (!purchased || lead.status === 'voided' || lead.status === 'withdrawn') return null;
  if (purchased === 'yes' && lead.status !== 'won') return 'unreported-sale';
  if (purchased === 'no' && lead.status === 'won') return 'disputed-sale';
  const paid = lead.followUp?.pricePaidCents;
  const deal = lead.dealValueCents;
  if (
    purchased === 'yes' &&
    lead.status === 'won' &&
    Number.isInteger(paid) &&
    Number.isInteger(deal)
  ) {
    const base = Math.max(paid, deal);
    if (base > 0 && Math.abs(paid - deal) / base > PRICE_TOLERANCE) return 'price-mismatch';
  }
  return null;
}

export function validateReviewResponse(input) {
  const text = typeof input?.text === 'string' ? input.text.trim() : '';
  if (!text) return { value: null, error: 'Write a response' };
  if (text.length > RESPONSE_TEXT_MAX)
    return { value: null, error: `At most ${RESPONSE_TEXT_MAX} characters` };
  return { value: text, error: null };
}

/** Average rating (one decimal) and count of published reviews. */
export function reviewSummary(reviews) {
  const shown = reviews.filter(r => r.status === 'published');
  if (!shown.length) return { count: 0, average: null };
  const sum = shown.reduce((n, r) => n + r.rating, 0);
  return { count: shown.length, average: Math.round((sum / shown.length) * 10) / 10 };
}

/**
 * Whether a recommendation of this listing can earn money (CR-36): the
 * vendor's reward program pays a percentage or a flat amount. Shown as
 * "May earn a referral reward" wherever the recommendation appears.
 */
export function rewardMayEarn(listing) {
  const program = listing?.rewardProgram;
  return Boolean(
    program && program.active !== false && (program.type === 'percent' || program.type === 'flat')
  );
}

// ---------------------------------------------------------------------------
// Fee computation (D68 items 1 to 4; M4)
// ---------------------------------------------------------------------------

function pctOf(cents, percent) {
  return Math.round((cents * percent) / 100);
}

/**
 * Split the lead fee of a won lead, in integer cents (design section 10):
 *
 *   lead fee         the category's fixed amount, or its percentage of the deal value,
 *                    at most the platform's largest lead fee
 *   organization fee the category's fixed amount, or its percentage of the lead fee,
 *                    at most the platform's largest organization fee and the lead fee
 *     platform fee   the platform's percentage of the organization fee, within its fixed
 *                    minimum and maximum, never more than the organization fee
 *     org share      the organization fee less the platform fee
 *   reward           the recommender's money reward (M5), from what is left
 *   balance          the rest, to the salesperson or broker; not charged when no
 *                    salesperson is assigned or the vendor sold the deal itself
 *
 * The vendor pays the organization fee, the reward and any charged balance.
 *
 * @param {{ leadFee, orgFee }} feeSnapshot - the fees in force when the lead was created
 * @param {{ dealValueCents?, policy?, salesperson?, rewardCents? }} opts
 */
export function computeFeeSplit(
  feeSnapshot,
  { dealValueCents = null, policy = DEFAULT_FEE_POLICY, salesperson = null, rewardCents = 0 } = {}
) {
  const lead = feeSnapshot?.leadFee;
  let leadFeeCents = 0;
  if (lead?.type === 'fixed') leadFeeCents = lead.amountCents;
  else if (lead?.type === 'percent' && Number.isInteger(dealValueCents))
    leadFeeCents = pctOf(dealValueCents, lead.percent);
  leadFeeCents = Math.max(0, Math.min(leadFeeCents, policy.maxLeadFeeCents));

  const org = feeSnapshot?.orgFee;
  let orgFeeCents = 0;
  if (org?.type === 'fixed') orgFeeCents = org.amountCents;
  else if (org?.type === 'percent') orgFeeCents = pctOf(leadFeeCents, org.percent);
  orgFeeCents = Math.max(0, Math.min(orgFeeCents, policy.maxOrgFeeCents, leadFeeCents));

  let platformFeeCents = pctOf(orgFeeCents, policy.platformFeePercent);
  platformFeeCents = Math.max(
    policy.platformFeeMinCents,
    Math.min(platformFeeCents, policy.platformFeeMaxCents)
  );
  platformFeeCents = Math.min(platformFeeCents, orgFeeCents);
  const orgShareCents = orgFeeCents - platformFeeCents;

  const afterOrg = leadFeeCents - orgFeeCents;
  const reward = Math.max(0, Math.min(Number.isInteger(rewardCents) ? rewardCents : 0, afterOrg));
  const balanceCents = afterOrg - reward;
  const balanceCharged = Boolean(salesperson && !salesperson.self) && balanceCents > 0;
  return {
    leadFeeCents,
    orgFeeCents,
    platformFeeCents,
    orgShareCents,
    rewardCents: reward,
    balanceCents: balanceCharged ? balanceCents : 0,
    balanceWaivedCents: balanceCharged ? 0 : balanceCents,
    vendorPaysCents: orgFeeCents + reward + (balanceCharged ? balanceCents : 0),
  };
}

/**
 * Ledger entry statuses: posted to the vendor's next invoice, invoiced, paid,
 * credited, disputed after invoicing, or no charge (an attributed win that
 * earns its recommender credits or attribution but costs the vendor nothing).
 */
export const LEDGER_STATUSES = [
  'pending',
  'invoiced',
  'paid',
  'credited',
  'disputed',
  'uncollectible',
  'no-charge',
];
/** A payout share's states: waiting for the invoice, held for a payout account, paid, or failed. */
export const PAYOUT_STATES = ['waiting', 'held', 'paid', 'failed', 'none'];

// ---------------------------------------------------------------------------
// Recommender reward programs (D68 item 4; M5)
// ---------------------------------------------------------------------------

export const REWARD_TYPES = ['percent', 'flat', 'credits', 'attribution'];

/**
 * What a won lead's recommender earns under the listing's program, in cents
 * of money (credits and attribution earn no money through the platform).
 * Nothing when the lead was not attributed, the recommender owns the vendor
 * (no one earns on their own leads, CR-36), the category's review policy
 * keeps money rewards off (licensed), or the program is a percentage in an
 * investment organization (flat only, D68 item 5). A money reward is at most
 * the platform's largest reward share of the lead fee, and never more than
 * what is left after the organization fee.
 */
export function rewardFor(
  lead,
  listing,
  {
    leadFeeCents = 0,
    afterOrgCents = 0,
    vendorOwnerId = null,
    investment = false,
    policy = DEFAULT_FEE_POLICY,
  } = {}
) {
  const program = listing?.rewardProgram;
  if (!lead?.recommenderId || !program || program.active === false)
    return { cents: 0, detail: null };
  if (lead.recommenderId === vendorOwnerId || lead.recommenderId === lead.memberId) {
    return { cents: 0, detail: { type: program.type, blocked: 'self' } };
  }
  if (program.type === 'credits')
    return { cents: 0, detail: { type: 'credits', credits: program.credits || null } };
  if (program.type === 'attribution') return { cents: 0, detail: { type: 'attribution' } };
  const reviewPolicy = REVIEW_POLICIES[lead.feeSnapshot?.reviewPolicy] || REVIEW_POLICIES.standard;
  if (!reviewPolicy.moneyRewards)
    return { cents: 0, detail: { type: program.type, blocked: 'licensed' } };
  if (program.type === 'percent' && investment)
    return { cents: 0, detail: { type: 'percent', blocked: 'investment' } };
  let cents =
    program.type === 'percent' ? pctOf(leadFeeCents, program.percent) : program.amountCents || 0;
  cents = Math.max(0, Math.min(cents, pctOf(leadFeeCents, policy.maxRewardPercent), afterOrgCents));
  return { cents, detail: { type: program.type, cents } };
}

export const REWARD_CREDITS_MAX = 200;
export const REWARD_TERMS_MAX = 1000;

/**
 * Validate a vendor's reward program for one listing (D68 item 4). `none`
 * removes it. A percentage is of the lead fee, within the platform's largest
 * reward share; investment organizations take flat amounts only (D68 item 5);
 * money programs are refused where the listing's review policy keeps money
 * rewards off (licensed categories, CR-37) until counsel clears them.
 */
export function validateRewardProgram(
  input,
  { investment = false, moneyRewards = true, policy = DEFAULT_FEE_POLICY } = {}
) {
  const fields = {};
  const type = input?.type;
  if (type === 'none') return { value: null, fields };
  if (!REWARD_TYPES.includes(type)) {
    fields.type = 'Choose a percentage, a flat amount, credits, attribution only, or none';
    return { value: null, fields };
  }
  const value = { type, active: input.active !== false };
  if ((type === 'percent' || type === 'flat') && !moneyRewards) {
    fields.type =
      'Money rewards are off in licensed categories until counsel clears them; offer credits or attribution';
  }
  if (type === 'percent') {
    if (investment)
      fields.type =
        'Investment organizations take flat amounts only (docs/Decisions.md D68 item 5)';
    else if (!isPercent(input.percent, policy.maxRewardPercent) || input.percent <= 0) {
      fields.percent = `Enter a percentage of the lead fee above 0 and up to ${policy.maxRewardPercent}`;
    } else value.percent = input.percent;
  }
  if (type === 'flat') {
    if (
      !isWholeCents(input.amountCents) ||
      input.amountCents <= 0 ||
      input.amountCents > policy.maxLeadFeeCents
    ) {
      fields.amountCents = `Enter an amount from 0.01 to ${money(policy.maxLeadFeeCents)}`;
    } else value.amountCents = input.amountCents;
  }
  if (type === 'credits') {
    const credits = cleanText(input.credits, REWARD_CREDITS_MAX);
    if (credits.length < 2 || credits.length > REWARD_CREDITS_MAX) {
      fields.credits = `Say what a recommender gets, in 2 to ${REWARD_CREDITS_MAX} characters`;
    } else value.credits = credits;
  }
  if (input.terms !== undefined && input.terms !== null && input.terms !== '') {
    if (typeof input.terms !== 'string' || input.terms.length > REWARD_TERMS_MAX) {
      fields.terms = `At most ${REWARD_TERMS_MAX} characters`;
    } else value.terms = input.terms.trim();
  }
  return { value: Object.keys(fields).length ? null : value, fields };
}

// ---------------------------------------------------------------------------
// Labels and display helpers (front end only)
// ---------------------------------------------------------------------------

export const REVIEW_POLICY_LABELS = {
  standard: {
    label: 'Standard',
    help: 'An org admin or a marketplace approver approves.',
  },
  restricted: {
    label: 'Restricted',
    help: 'Only an org admin approves, with a note on what was checked.',
  },
  regulated: {
    label: 'Regulated',
    help: 'Every listed license, permit or document is validated before approval; approvers may approve.',
  },
  licensed: {
    label: 'Licensed',
    help: 'Only an org admin approves, after validating every license, with a note. Recommender rewards are not paid in money.',
  },
};

export const REQUIREMENT_KIND_LABELS = {
  license: 'License',
  permit: 'Permit',
  insurance: 'Insurance',
  document: 'Document',
  other: 'Other',
};

export const LISTING_STATUS_LABELS = {
  invited: 'Invited',
  pending: 'Waiting for review',
  approved: 'Listed',
  declined: 'Declined',
  suspended: 'Suspended',
  removed: 'Removed',
  withdrawn: 'Withdrawn',
};

export const LISTING_ACTION_LABELS = {
  approve: 'Approve',
  decline: 'Decline',
  suspend: 'Suspend',
  reinstate: 'Reinstate',
  remove: 'Remove',
  withdraw: 'Withdraw',
};

export const PRICE_UNIT_LABELS = {
  'one-time': 'one time',
  hour: 'per hour',
  month: 'per month',
  year: 'per year',
  project: 'per project',
};

export function listingStatusTone(status) {
  if (status === 'approved') return 'success';
  if (status === 'pending' || status === 'invited') return 'warning';
  if (status === 'suspended' || status === 'declined' || status === 'removed') return 'danger';
  return 'neutral';
}

/** "150.00" style, no currency sign. */
export function centsToInput(cents) {
  return Number.isInteger(cents) ? money(cents) : '';
}

export function formatUsd(cents) {
  return `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** How a service's price reads on the vendor page. */
export function priceLabel(service) {
  if (service.priceType === 'quote' || !Number.isInteger(service.priceCents))
    return 'Contact for a quote';
  const unit =
    service.unit && service.unit !== 'one-time' ? ` ${PRICE_UNIT_LABELS[service.unit]}` : '';
  return `${service.priceType === 'from' ? 'From ' : ''}${formatUsd(service.priceCents)}${unit}`;
}

/** How a category's fee reads to an admin or an applying vendor. */
export function feeLabel(fee, of) {
  if (!fee) return 'None';
  if (fee.type === 'percent') return `${fee.percent}% of ${of}`;
  return formatUsd(fee.amountCents);
}

/** A services list back to CSV, for the vendor to edit in a spreadsheet. */
export function servicesToCsv(services) {
  const esc = v => {
    const s = String(v ?? '');
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const rows = [CSV_COLUMNS.join(',')];
  for (const s of services || []) {
    rows.push(
      [
        s.name,
        s.description,
        s.priceType,
        s.priceType === 'quote' ? '' : centsToInput(s.priceCents),
        s.unit,
      ]
        .map(esc)
        .join(',')
    );
  }
  return `${rows.join('\n')}\n`;
}

export const LEAD_STATUS_LABELS = {
  new: 'New',
  qualified: 'Qualified',
  won: 'Won',
  lost: 'Closed lost',
  withdrawn: 'Withdrawn',
  voided: 'Voided',
};

export const LEAD_ACTION_LABELS = {
  qualify: 'Mark qualified',
  win: 'Mark won',
  lose: 'Mark lost',
  assign: 'Assign',
  withdraw: 'Leave the list',
  void: 'Void',
};

export const LOST_REASON_LABELS = {
  'no-response': 'No response',
  'not-a-fit': 'Not a fit',
  'chose-another': 'Chose another provider',
  price: 'Price',
  other: 'Other',
};

export function leadStatusTone(status) {
  if (status === 'won') return 'success';
  if (status === 'new' || status === 'qualified') return 'warning';
  if (status === 'lost' || status === 'voided') return 'danger';
  return 'neutral';
}

/** The vendor's own leads as CSV; contact details only where consent stands. */
export function leadsToCsv(leads) {
  const esc = v => {
    const s = String(v ?? '');
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const rows = [
    ['created', 'status', 'service', 'name', 'email', 'phone', 'deal_value', 'salesperson'].join(
      ','
    ),
  ];
  for (const l of leads) {
    rows.push(
      [
        l.createdAt,
        l.status,
        l.serviceName,
        l.contact?.name,
        l.contact?.email,
        l.contact?.phone,
        Number.isInteger(l.dealValueCents) ? centsToInput(l.dealValueCents) : '',
        l.salespersonName,
      ]
        .map(esc)
        .join(',')
    );
  }
  return `${rows.join('\n')}\n`;
}

export const PURCHASE_LABELS = { yes: 'Yes', no: 'No', 'not-yet': 'Not yet' };

export const MISMATCH_LABELS = {
  'unreported-sale': 'The member says they bought; the vendor did not record a won deal',
  'disputed-sale': 'The vendor recorded a won deal; the member says they did not buy',
  'price-mismatch': 'The member and the vendor report prices more than 20% apart',
};

export const HIDE_REASON_LABELS = {
  abuse: 'Abuse or harassment',
  'personal-data': 'Personal data',
  'off-topic': 'Off-topic',
  'conflict-of-interest': 'Conflict of interest',
};

/** "4.5 of 5 (12 reviews)" */
export function ratingLabel(summary) {
  if (!summary?.count) return 'No reviews yet';
  return `${summary.average} of 5 (${summary.count} review${summary.count === 1 ? '' : 's'})`;
}

export const LEDGER_STATUS_LABELS = {
  pending: 'On the next invoice',
  invoiced: 'Invoiced',
  paid: 'Paid',
  credited: 'Credited',
  disputed: 'Disputed',
  uncollectible: 'Uncollectible',
  'no-charge': 'No charge',
};

export const PAYOUT_STATE_LABELS = {
  waiting: 'Waiting for the vendor to pay',
  held: 'Held until payouts are set up',
  paid: 'Paid out',
  failed: 'Failed',
  none: '-',
};

export const REWARD_TYPE_LABELS = {
  percent: 'A percentage of the lead fee',
  flat: 'A flat amount per won deal',
  credits: 'Credits with the vendor',
  attribution: 'Attribution only (credited, nothing paid)',
};

/** How a reward program reads to members (CR-36). */
export function rewardProgramLabel(program) {
  if (!program || program.active === false) return null;
  if (program.type === 'percent')
    return `Recommenders earn ${program.percent}% of the lead fee on deals they bring`;
  if (program.type === 'flat')
    return `Recommenders earn ${formatUsd(program.amountCents)} per deal they bring`;
  if (program.type === 'credits') return `Recommenders receive: ${program.credits}`;
  return 'Recommenders are credited on the deals they bring';
}
